import { createFileRoute } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { therapies } from "@/components/site/Therapies";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  { q: "What therapies does Cherry Hill Recovery Center use?", a: "We use six evidence-based approaches: Cognitive Behavioral Therapy, Dialectical Behavior Therapy, trauma-informed care, motivational interviewing, family therapy and relapse prevention, alongside psychiatric care and medication-assisted treatment when appropriate." },
  { q: "Is CBT or DBT better for addiction?", a: "Both are effective and most patients receive elements of each. CBT targets the thoughts and situations that lead to using; DBT builds skills for managing intense emotions. Your therapist decides the balance based on your evaluation." },
  { q: "Do you treat trauma and PTSD alongside addiction?", a: "Yes. All clinical staff are trained in trauma-informed care, and co-occurring PTSD, anxiety and depression are treated in the same program as substance use, with psychiatric oversight." },
  { q: "Can my family participate in treatment?", a: "Yes. Family therapy sessions are part of PHP and IOP, and we offer family programming and a free family guide for loved ones." },
  { q: "Are these therapies available in Virtual IOP?", a: "CBT, DBT, trauma-informed care, motivational interviewing and relapse prevention are all delivered in our Virtual IOP for New Jersey residents. Family sessions are typically scheduled in person." },
  { q: "How do I know which therapy is right for me?", a: "You do not have to decide. Your first step is a psychiatric and drug & alcohol evaluation; from there our clinical team builds an individualized plan and adjusts it as you progress." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const TITLE = "Evidence-Based Addiction Therapies in Cherry Hill, NJ";
const DESC =
  "CBT, DBT, trauma-informed care, motivational interviewing, family therapy and relapse prevention in PHP, IOP and Virtual IOP at Cherry Hill Recovery Center.";

export const Route = createFileRoute("/therapies-cherry-hill-nj")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(FAQ_SCHEMA) }],
    links: [{ rel: "canonical", href: "https://cherryhillrecoverycenter.com/therapies-cherry-hill-nj" }],
  }),
  component: TherapiesPage,
});

function TherapiesPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-20 md:py-24">
        <div className="container max-w-4xl">
          <h1 className="font-display text-3xl md:text-5xl font-bold text-primary text-balance">
            Evidence-Based Therapies in Cherry Hill, NJ
          </h1>
          <p className="mt-5 text-lg text-muted-foreground">
            Every patient in PHP, IOP or Virtual IOP works with licensed therapists using proven approaches.
            Co-occurring anxiety, depression and trauma are treated alongside substance use under the oversight
            of our board-certified psychiatrist.
          </p>
          <div className="mt-12 space-y-8">
            {therapies.map(({ icon: Icon, title, desc, usedIn, href }) => (
              <section
                key={title}
                id={href.split("#")[1]}
                className="scroll-mt-28 bg-card border border-border rounded-2xl p-8 shadow-soft"
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h2 className="font-display text-2xl font-semibold text-primary">{title}</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed">{desc}</p>
                <p className="mt-4 text-sm text-muted-foreground">
                  <span className="font-semibold text-primary">Used in:</span> {usedIn}
                </p>
              </section>
            ))}
          </div>
          <section className="mt-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary text-center">
              Frequently Asked Questions About Our Therapies
            </h2>
            <Accordion type="single" collapsible className="mt-8 space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <h3 className="text-primary font-semibold text-base">{f.q}</h3>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-muted-foreground text-[15px] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-full px-8 h-12 font-semibold">
              <a href="tel:8562003127"><Phone className="h-4 w-4" /> Call 856-200-3127</a>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
